import { useTranslator } from '@bluebeam/react-i18n';
/* eslint-disable no-nested-ternary */
import { Button, Modal, ModalContent, ModalFooter } from 'components/lib';
import OverlayFooter from 'components/Overlays/OverlayFooter';
import OverlayHeader from 'components/Overlays/OverlayHeader';
import OverlaySection from 'components/Overlays/OverlaySection';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import Tabs from 'components/Tabs';
import Tab from 'components/Tabs/Tab';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import React, { useEffect, useRef, useState } from 'react';
import { ViewRFI } from 'records/interactions/ViewRFI';
import { RFI } from 'records/RFI';
import { RFIForwardActivity } from 'records/RFIForwardActivity';
import DownloadRFI from '../DownloadRFI';
import ForwardRFIDiscussion from './ForwardRFIDiscussion';
import ForwardForm from './FowardForm';
import OfficialQuestionSection from './OfficialQuestionSection';
import RFIRespContent from './RFIRespContent';
import ViewHistorySection from './ViewHistorySection';
import ViewOfficialQuestionSection from './ViewOfficialQuestionSection';
import ViewProjectDetailsSection from './ViewProjectDetailsSection';
import ViewReqBySection from './ViewReqBySection';
import {
  useViewRfiMachine,
  ViewRFIEventsEnum,
  ViewRFIStatesEnum,
} from './ViewRFIMachine';
import {
  AttachDiscussionToResponse,
  CreateOfficialResponseButton,
  ForwardInstructions,
  ForwardNotificationBanner,
  LatestDraftBanner,
  LatestVersionBanner,
} from './ViewRFIMachine/viewRfiElements';
import { RFIResponseRoleEnum } from './ViewRFIMachine/viewRfiMachineTypes';

type Props = {
  interaction: ViewRFI;
  onClose: () => void;
  onBackClick: () => void;
  onEditPlacement: () => void;
  onRfiSelection: (id: string) => void;
};

const ViewRFIOverlayContent: React.FC<Props> = ({
  interaction,
  onBackClick,
  onClose,
  onEditPlacement,
  onRfiSelection,
}: Props) => {
  const { translate } = useTranslator([
    'formLabels',
    'buttons',
    'rfiPage',
    'mapView',
  ]);
  const [selectedTab, setSelectedTab] = useState('Overview');
  const { viewRfiMachineState, viewRfiMachineService } = useViewRfiMachine();

  const { responding, placing, backArrow, rfi: interactionRFI } = interaction;

  const {
    rfi,
    role,
    officialResponse,
    forwardMessage,
    userEmails,
    forwardReply,
    selectedDiscussionIds,
    project,
    currentUser,
    rfiRevision,
    rfiVersionList,
  } = viewRfiMachineState.context;

  const {
    [FeatureFlag.RfiForwarding]: isRfiForwardingEnabled,
    [FeatureFlag.RfiRevisions]: isRfiRevisioningEnabled,
  } = useFeatureFlags();

  const canRespond =
    role === RFIResponseRoleEnum.admin || role === RFIResponseRoleEnum.assignee;

  const canReplyToForward =
    isRfiForwardingEnabled && rfi?.forwardactivity.size === 1;

  const showTabs =
    isRfiForwardingEnabled &&
    !viewRfiMachineState.matches(ViewRFIStatesEnum.creatingRfiVersion) &&
    !viewRfiMachineState.matches(ViewRFIStatesEnum.creatingRfiDraft) &&
    !viewRfiMachineState.matches(ViewRFIStatesEnum.reopeningRfi) &&
    !viewRfiMachineState.matches(ViewRFIStatesEnum.savingRfiDraft) &&
    !viewRfiMachineState.matches(ViewRFIStatesEnum.updatingRfiDraft) &&
    !viewRfiMachineState.matches(ViewRFIStatesEnum.deletingRfiDraft) &&
    !viewRfiMachineState.matches(ViewRFIStatesEnum.publishingRfiDraft);

  useEffect(() => {
    viewRfiMachineService.send(ViewRFIEventsEnum.RFI_SELECTED, {
      data: interactionRFI,
    });
    setSelectedTab('Overview');
  }, [interactionRFI.id]);

  useEffect(() => {
    onRfiSelection(rfi.id);
  }, [rfi.id]);

  const onAttachDiscussionClick = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.ATTACH_DISCUSSION);
    setSelectedTab('Overview');
  };

  const onCancelClick = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.CANCEL);
    onClose();
  };

  const onForwardClick = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.FORWARD);
    setSelectedTab('Discussion');
  };

  const sendSendEvent = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.SEND);
  };

  const sendCancelEvent = () => {
    if (rfi.discussionattachmentids.size > 0) {
      viewRfiMachineService.send({
        type: ViewRFIEventsEnum.CANCEL_WITH_DISCUSSION_ATTACHMENT,
        data: { id: rfi.discussionattachmentids.first() },
      });
    }
    viewRfiMachineService.send(ViewRFIEventsEnum.CANCEL);
  };

  const sendForwardEvent = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.FORWARD);
  };

  const sendRespondEvent = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.RESPOND);
  };

  const sendDiscussEvent = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.DISCUSS);
    setSelectedTab('Discussion');
  };

  const sendReopenEvent = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.REOPEN_RFI);
  };

  const sendSaveEvent = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.SAVE);
  };

  const sendDeleteEvent = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.DELETE);
  };

  const sendDeleteConfirmEvent = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.DELETE_CONFIRM);
  };

  const handleDiscussionSelection = (activityId: string) => {
    const newDiscussionIds = selectedDiscussionIds.contains(activityId)
      ? selectedDiscussionIds.remove(activityId)
      : selectedDiscussionIds.add(activityId);
    viewRfiMachineService.send(ViewRFIEventsEnum.UPDATE_SELECTED_DISCUSSIONS, {
      data: { ids: newDiscussionIds },
    });
  };

  const showLatestVersion = () => {
    const latestVersion: RFI =
      viewRfiMachineState.context.rfiVersionList.first();
    onRfiSelection(latestVersion.id);
  };

  const officialResponseFormRef = useRef<HTMLDivElement | null>(null);

  const scrollFormIntoView = () => {
    if (officialResponseFormRef.current !== null) {
      officialResponseFormRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const rfiIsInvalid = (rfiRevision: RFI) => {
    return (
      rfiRevision.title.length <= 0 ||
      rfiRevision.question.length <= 0 ||
      rfiRevision.assigneeEmail.length <= 0 ||
      rfiRevision.submittingcompany.trim().length <= 0 ||
      rfiRevision.submittingname.trim().length <= 0 ||
      rfiRevision.submittingemail.trim().length <= 0 ||
      rfiRevision.duedate <= 0
    );
  };

  const canEditPlacement = project.permissions.editAssignee;

  useEffect(() => {
    if (
      viewRfiMachineState.matches(ViewRFIStatesEnum.creatingOfficialResponse)
    ) {
      scrollFormIntoView();
    }

    if (viewRfiMachineState.matches(ViewRFIStatesEnum.creatingRfiVersion)) {
      const rfiDraftForm = document.getElementById('rfi-draft-form');
      if (rfiDraftForm) {
        rfiDraftForm.scrollTop = 0;
      }
    }

    if (viewRfiMachineState.matches(ViewRFIStatesEnum.done)) {
      onClose();
    }
  }, [viewRfiMachineState.value]);

  const isLoading =
    viewRfiMachineState.matches(ViewRFIStatesEnum.reopeningRfi) ||
    viewRfiMachineState.matches(ViewRFIStatesEnum.savingRfiDraft) ||
    viewRfiMachineState.matches(ViewRFIStatesEnum.publishingRfiDraft) ||
    viewRfiMachineState.matches(ViewRFIStatesEnum.sendingOfficialResponse) ||
    viewRfiMachineState.matches(ViewRFIStatesEnum.sendingForwardMessage) ||
    viewRfiMachineState.matches(ViewRFIStatesEnum.sendingForwardReply) ||
    viewRfiMachineState.matches(ViewRFIStatesEnum.updatingRfiDraft) ||
    viewRfiMachineState.matches(ViewRFIStatesEnum.deletingRfiDraft);

  const renderContent = () => {
    if (
      viewRfiMachineState.matches(ViewRFIStatesEnum.creatingRfiVersion) ||
      viewRfiMachineState.matches(ViewRFIStatesEnum.savingRfiDraft) ||
      viewRfiMachineState.matches(ViewRFIStatesEnum.reopeningRfi)
    ) {
      return (
        <>
          <div id="rfi-draft-form" className="column justify-between oa">
            <div className="mx3 my2">
              <OfficialQuestionSection />
              <ViewReqBySection />
              <ViewProjectDetailsSection project={project} />
            </div>
          </div>
          <OverlayFooter
            id="view-rfi-overlay-overlay-footer"
            confirmText={translate('buttons:send')}
            isLoading={isLoading}
            onConfirmClick={sendSendEvent}
            confirmDisabled={rfiIsInvalid(rfiRevision.rfi)}
            onCancelClick={sendCancelEvent}
          >
            <Button
              id="view-rfi-overlay-overlay-footer-save-draft-btn"
              variant="secondary"
              size="large"
              onClick={sendSaveEvent}
              disabled={isLoading}
            >
              {translate('buttons:save')}
            </Button>
          </OverlayFooter>
        </>
      );
    }

    if (
      viewRfiMachineState.matches(ViewRFIStatesEnum.creatingRfiDraft) ||
      viewRfiMachineState.matches(ViewRFIStatesEnum.updatingRfiDraft) ||
      viewRfiMachineState.matches(ViewRFIStatesEnum.deletingRfiDraft) ||
      viewRfiMachineState.matches(ViewRFIStatesEnum.deletingRfiDraftConfirm) ||
      viewRfiMachineState.matches(ViewRFIStatesEnum.publishingRfiDraft)
    ) {
      return (
        <>
          {viewRfiMachineState.matches(
            ViewRFIStatesEnum.deletingRfiDraftConfirm
          ) && (
            <Modal
              title={translate('mapView:viewRFIOverlayContent.deleteRFIDraft')}
              size="small"
              onDismiss={sendCancelEvent}
            >
              <ModalContent>
                <div className="light font-size-5 black-2 mb3">
                  {translate(
                    'mapView:viewRFIOverlayContent.permanentlyRemovedText'
                  )}
                </div>
                <div className="medium font-size-5 black-2">
                  {translate('mapView:viewRFIOverlayContent.areYouSure')}
                </div>
              </ModalContent>
              <ModalFooter>
                <Button
                  id="view-rfi-overlay-overlay-footer-cancel-btn"
                  size="large"
                  variant="tertiary"
                  onClick={sendCancelEvent}
                >
                  {translate('buttons:cancel')}
                </Button>
                <Button
                  id="view-rfi-overlay-overlay-footer-delete-draft-btn"
                  variant="destructive"
                  size="large"
                  disabled={isLoading}
                  onClick={sendDeleteEvent}
                >
                  {translate('buttons:delete')}
                </Button>
              </ModalFooter>
            </Modal>
          )}
          <div className="column justify-between oa">
            <div className="mx3 my2">
              <OfficialQuestionSection />
              <ViewReqBySection />
              <ViewProjectDetailsSection project={project} />
            </div>
          </div>
          <OverlayFooter
            id="view-rfi-overlay-overlay-footer"
            confirmText={translate('buttons:send')}
            onConfirmClick={sendSendEvent}
            confirmDisabled={rfiIsInvalid(rfiRevision.rfi)}
            secondaryText={translate('buttons:save')}
            hideSecondary={false}
            isLoading={isLoading}
            onSecondaryClick={sendSaveEvent}
            hideCancel
          >
            <Button
              id="view-rfi-overlay-overlay-footer-save-draft-btn"
              variant="destructive"
              size="large"
              disabled={isLoading}
              onClick={sendDeleteConfirmEvent}
            >
              {translate('mapView:viewRFIOverlayContent.deleteDraft')}
            </Button>
          </OverlayFooter>
        </>
      );
    }

    if (
      viewRfiMachineState.matches(ViewRFIStatesEnum.pendingOfficialResponse)
    ) {
      const firstForwardActivity: RFIForwardActivity =
        rfi.forwardactivity.first() || new RFIForwardActivity();

      return (
        <>
          <div className="column justify-between oa">
            <div className="mx3 my2">
              <ViewOfficialQuestionSection onRfiSelection={onRfiSelection} />
              <ViewReqBySection />
              <ViewProjectDetailsSection project={project} />
              <ViewHistorySection />
            </div>
          </div>
          {role === RFIResponseRoleEnum.consultant && canReplyToForward && (
            <ForwardNotificationBanner
              onClick={sendDiscussEvent}
              forwardActivity={firstForwardActivity}
              forwardedBy={firstForwardActivity.user}
            />
          )}
          {(role === RFIResponseRoleEnum.admin ||
            role === RFIResponseRoleEnum.assignee) && (
            <>
              <CreateOfficialResponseButton onClick={sendRespondEvent} />
              <OverlayFooter
                id="view-rfi-overlay-overlay-footer"
                isLoading={isLoading}
                secondaryText={translate('buttons:forward')}
                onSecondaryClick={sendDiscussEvent}
                hideSecondary={
                  rfi?.forwardactivity.size > 0 || !isRfiForwardingEnabled
                }
                hideConfirm
                hideCancel
              >
                <DownloadRFI rfi={rfi} />
              </OverlayFooter>
            </>
          )}
          {(role === RFIResponseRoleEnum.consultant ||
            role === RFIResponseRoleEnum.unauthorized) && (
            <OverlayFooter id="view-rfi-overlay-footer" hideConfirm hideCancel>
              <DownloadRFI rfi={rfi} />
            </OverlayFooter>
          )}
        </>
      );
    }

    if (
      viewRfiMachineState.matches(ViewRFIStatesEnum.creatingOfficialResponse) ||
      viewRfiMachineState.matches(ViewRFIStatesEnum.sendingOfficialResponse) ||
      viewRfiMachineState.matches(
        ViewRFIStatesEnum.viewingAttachedDiscussions
      ) ||
      viewRfiMachineState.matches(
        ViewRFIStatesEnum.attachingDiscussionToResponse
      ) ||
      viewRfiMachineState.matches(ViewRFIStatesEnum.deletingAttachedDiscussion)
    ) {
      return (
        <>
          {viewRfiMachineState.matches(
            ViewRFIStatesEnum.viewingAttachedDiscussions
          ) && (
            <Modal
              title={translate(
                'mapView:viewRFIOverlayContent.attachDiscussion'
              )}
              size="medium"
              onDismiss={sendCancelEvent}
            >
              <ModalContent>
                <div className="regular size-16 black-2 mb3">
                  {translate('mapView:viewRFIOverlayContent.allMessages')}
                </div>
                <div className="px3 b-2 oa">
                  <ForwardRFIDiscussion
                    rfi={rfi}
                    selectedDiscussionIds={selectedDiscussionIds}
                    setSelectedDiscussionIds={handleDiscussionSelection}
                    canAttach={rfi?.discussionattachmentids.size === 0}
                  />
                </div>
              </ModalContent>
              <ModalFooter>
                <Button
                  id="map-select-layer-cancel-btn"
                  size="large"
                  variant="tertiary"
                  onClick={sendCancelEvent}
                >
                  {translate('buttons:cancel')}
                </Button>
                <Button
                  id="map-select-layer-confirm-btn"
                  size="large"
                  variant="primary"
                  onClick={onAttachDiscussionClick}
                  disabled={selectedDiscussionIds?.size === 0}
                >
                  {translate('buttons:attach')}
                </Button>
              </ModalFooter>
            </Modal>
          )}
          <div className="column justify-between oa">
            <div className="mx3 my2">
              <ViewOfficialQuestionSection onRfiSelection={onRfiSelection} />
              <div ref={officialResponseFormRef} />
              <OverlaySection label={translate('formLabels:officialResponse')}>
                <ForwardForm
                  response={officialResponse}
                  title={translate('formLabels:response')}
                  canAccessDiscussionModal={
                    rfi?.forwardactivity.size > 0 && !!isRfiForwardingEnabled
                  }
                />
              </OverlaySection>
              <ViewReqBySection />
              <ViewProjectDetailsSection project={project} />
              <ViewHistorySection />
            </div>
          </div>
          <OverlayFooter
            id="view-rfi-overlay-footer"
            confirmText={translate('buttons:send')}
            onConfirmClick={sendSendEvent}
            isLoading={isLoading}
            confirmDisabled={officialResponse.message.length === 0}
            hideConfirm={rfi.answer !== null || !canRespond}
            onCancelClick={sendCancelEvent}
          />
        </>
      );
    }

    if (viewRfiMachineState.matches(ViewRFIStatesEnum.pendingForward)) {
      return (
        <div className="column justify-between oa h100 pos-r">
          <ForwardInstructions>
            <Button variant="primary" size="large" onClick={onForwardClick}>
              {translate('buttons:forward')}
            </Button>
          </ForwardInstructions>
          <OverlayFooter
            id="view-rfi-overlay-overlay-footer"
            secondaryText={translate('buttons:forward')}
            onSecondaryClick={sendForwardEvent}
            isLoading={isLoading}
            hideSecondary={rfi.answer !== null || !canRespond}
            hideConfirm
            hideCancel
          >
            <DownloadRFI rfi={rfi} />
          </OverlayFooter>
        </div>
      );
    }

    if (
      viewRfiMachineState.matches(ViewRFIStatesEnum.creatingForwardMessage) ||
      viewRfiMachineState.matches(ViewRFIStatesEnum.sendingForwardMessage)
    ) {
      return (
        <>
          <div className="column justify-between oa h100 pos-r">
            <ForwardInstructions>
              <div className="bgd-gray-2 w100 b-2 p3 mb6">
                <ForwardForm
                  response={forwardMessage}
                  recipients={userEmails}
                />
              </div>
            </ForwardInstructions>
          </div>
          <OverlayFooter
            id="view-rfi-overlay-overlay-footer"
            hideSecondary
            confirmText={translate('buttons:send')}
            isLoading={isLoading}
            onConfirmClick={sendSendEvent}
            onCancelClick={sendCancelEvent}
            confirmDisabled={
              forwardMessage?.forwardList.size === 0 ||
              forwardMessage?.message.length === 0
            }
          />
        </>
      );
    }

    if (viewRfiMachineState.matches(ViewRFIStatesEnum.pendingForwardReply)) {
      const canAttach =
        isRfiForwardingEnabled &&
        (role === RFIResponseRoleEnum.admin ||
          role === RFIResponseRoleEnum.assignee);

      return (
        <>
          <div className="column justify-between oa h100">
            <div className="mx3 mt2 mb4">
              <div className="column justify-start items-center h100 px3 black-2 font-size-5">
                <ForwardRFIDiscussion
                  rfi={rfi}
                  canAttach={canAttach}
                  selectedDiscussionIds={selectedDiscussionIds}
                  setSelectedDiscussionIds={handleDiscussionSelection}
                />
              </div>
            </div>
          </div>
          <OverlayFooter
            id="view-rfi-overlay-overlay-footer"
            hideSecondary
            isLoading={isLoading}
            confirmText={translate('buttons:reply')}
            onConfirmClickSync={() => {
              viewRfiMachineService.send(ViewRFIEventsEnum.REPLY);
              setSelectedTab('Discussion');
            }}
            onCancelClick={() => {
              sendCancelEvent();
              setSelectedTab('Overview');
            }}
          />
        </>
      );
    }

    if (
      viewRfiMachineState.matches(ViewRFIStatesEnum.pendingDiscussionAttachment)
    ) {
      const canAttach =
        isRfiForwardingEnabled &&
        (role === RFIResponseRoleEnum.admin ||
          role === RFIResponseRoleEnum.assignee);

      return (
        <>
          <div className="column justify-between oa grow-1">
            <div className="mx3 mt2 mb4">
              <div className="column justify-start items-center h100 px3 black-2 font-size-5">
                <ForwardRFIDiscussion
                  rfi={rfi}
                  canAttach={canAttach}
                  selectedDiscussionIds={selectedDiscussionIds}
                  setSelectedDiscussionIds={handleDiscussionSelection}
                />
              </div>
            </div>
          </div>
          {(role === RFIResponseRoleEnum.admin ||
            role === RFIResponseRoleEnum.assignee) && (
            <AttachDiscussionToResponse
              onClick={onAttachDiscussionClick}
              disabled={selectedDiscussionIds?.size === 0}
            />
          )}
          <OverlayFooter
            id="view-rfi-overlay-overlay-footer"
            isLoading={isLoading}
            hideSecondary
            hideConfirm
            onCancelClick={() => {
              sendCancelEvent();
              setSelectedTab('Overview');
            }}
          />
        </>
      );
    }

    if (
      viewRfiMachineState.matches(ViewRFIStatesEnum.creatingForwardReply) ||
      viewRfiMachineState.matches(ViewRFIStatesEnum.sendingForwardReply)
    ) {
      const hideConfirmButton =
        !rfi.forwardinglist.contains(currentUser.email) &&
        role !== RFIResponseRoleEnum.admin &&
        role !== RFIResponseRoleEnum.assignee;

      return (
        <>
          <div className="column justify-between oa h100">
            <div className="mx3 mt2 mb4 pt3">
              <div className="bgd-gray-2 w100 b-2 p3 mb3">
                <ForwardForm response={forwardReply} />
              </div>
            </div>
          </div>
          <OverlayFooter
            id="view-rfi-overlay-footer"
            isLoading={isLoading}
            confirmText={translate('buttons:send')}
            onConfirmClick={sendSendEvent}
            onCancelClick={sendCancelEvent}
            confirmDisabled={forwardReply.message.length === 0}
            hideConfirm={hideConfirmButton}
          />
        </>
      );
    }

    if (viewRfiMachineState.matches(ViewRFIStatesEnum.discussionAttached)) {
      return (
        <>
          <div className="column justify-between oa h100">
            <div className="mx3 mt2 mb4">
              <div className="column justify-start items-center h100 px3 black-2 font-size-5">
                <ForwardRFIDiscussion
                  rfi={rfi}
                  canAttach={false}
                  selectedDiscussionIds={selectedDiscussionIds}
                  setSelectedDiscussionIds={handleDiscussionSelection}
                />
              </div>
            </div>
          </div>
          <OverlayFooter
            id="view-rfi-overlay-overlay-footer"
            hideSecondary
            onCancelClick={onCancelClick}
            hideConfirm
          />
        </>
      );
    }

    if (
      viewRfiMachineState.matches(ViewRFIStatesEnum.closed) ||
      viewRfiMachineState.matches(
        ViewRFIStatesEnum.reopenedRfiOnViewerOrMapPage
      )
    ) {
      const renderEditPlacementFooter = () => (
        <OverlayFooter
          id="rfi-open-edit-placement"
          confirmText={translate('mapView:viewRFIOverlayContent.editPlacement')}
          onConfirmClick={onEditPlacement}
          confirmDisabled={!project.permissions.activeProject}
          hideCancel
        >
          <DownloadRFI rfi={rfi} />
        </OverlayFooter>
      );

      const renderReopenFooter = () => (
        <OverlayFooter
          id="view-rfi-overlay-footer"
          hideConfirm
          hideSecondary={
            !isRfiRevisioningEnabled ||
            viewRfiMachineState.context.role !== RFIResponseRoleEnum.admin ||
            rfi.nextversionid !== ''
          }
          secondaryText={translate('buttons:reopen')}
          onSecondaryClick={sendReopenEvent}
          secondaryDisabled={false}
          hideCancel
          isLoading={isLoading}
        >
          <DownloadRFI rfi={rfi} />
        </OverlayFooter>
      );

      const renderFooter = () => {
        switch (true) {
          case viewRfiMachineState.matches(
            ViewRFIStatesEnum.reopenedRfiOnViewerOrMapPage
          ) && canEditPlacement:
            return renderEditPlacementFooter();

          case !responding && placing && canEditPlacement:
            return renderEditPlacementFooter();

          default:
            return renderReopenFooter();
        }
      };

      return selectedTab === 'Overview' ? (
        <>
          <div className="column justify-between oa">
            <div className="mx3 my2">
              <ViewOfficialQuestionSection onRfiSelection={onRfiSelection} />
              <RFIRespContent />
              <ViewReqBySection />
              <ViewProjectDetailsSection project={project} />
              <ViewHistorySection />
            </div>
          </div>
          {renderFooter()}
        </>
      ) : (
        <>
          <div className="column justify-between oa h100">
            <div className="mx3 mt2 mb4">
              <div className="column justify-start items-center h100 px3 black-2 font-size-5">
                {rfi.answer && (
                  <>
                    <span className="inline-block pt8 font-size-7">
                      {translate('rfiPage:discussionClosed')}
                    </span>
                    <div
                      className="center pt3"
                      style={{ paddingBottom: '32px', width: '20rem' }}
                    >
                      {translate('rfiPage:discussionClosedSubtext')}
                    </div>
                  </>
                )}
                <ForwardRFIDiscussion
                  rfi={rfi}
                  canAttach={false}
                  selectedDiscussionIds={selectedDiscussionIds}
                  setSelectedDiscussionIds={handleDiscussionSelection}
                />
              </div>
            </div>
          </div>
          <OverlayFooter
            id="view-rfi-overlay-overlay-footer"
            hideSecondary
            onCancelClick={onCancelClick}
            hideConfirm
          />
        </>
      );
    }

    return <div />;
  };

  const getOverlayTitle = (): string => {
    if (viewRfiMachineState.matches(ViewRFIStatesEnum.creatingRfiVersion)) {
      const versionnumber =
        rfi.versionnumber > 0 ? `.${rfi.versionnumber}` : '';
      return `${translate('mapView:viewRFIOverlayContent.reopeningRFI')} ${
        rfi.number
      }${versionnumber}`;
    }
    return translate('mapView:viewRFIOverlayContent.rfiDetails');
  };

  const renderLatestVersionBanner = () => {
    if (rfi.nextversionid === '') {
      return null;
    }

    const latestRfi: RFI = rfiVersionList.first();
    const latestRfiVersionIsDraft = latestRfi.craeteddate === 0;

    if (rfi.id === latestRfi.id) {
      return null;
    }

    if (latestRfiVersionIsDraft) {
      return <LatestDraftBanner showLatestVersion={showLatestVersion} />;
    }

    return <LatestVersionBanner showLatestVersion={showLatestVersion} />;
  };

  return (
    <OverlayStdContainer
      id="view-rfi-overlay"
      style={{
        backgroundColor: 'var(--white-2)',
      }}
    >
      <OverlayHeader
        id="view-rfi-overlay-header"
        title={getOverlayTitle()}
        backArrow={backArrow || placing}
        onBackArrowClick={onBackClick}
        onCancelClick={onCancelClick}
      />
      {renderLatestVersionBanner()}
      {showTabs && (
        <div className="pt2 bgd-white" style={{ zIndex: 3 }}>
          <Tabs
            tabNames={['Overview', 'Discussion']}
            onChange={(nextTab: string) => {
              const event =
                nextTab === 'Discussion'
                  ? ViewRFIEventsEnum.DISCUSS
                  : ViewRFIEventsEnum.RESPOND;
              viewRfiMachineService.send(event);
              setSelectedTab(nextTab);
            }}
            selectedTab={selectedTab}
          >
            <Tab
              name="Overview"
              placeholder={translate('mapView:viewRFIOverlayContent.overview')}
            />
            <Tab
              name="Discussion"
              placeholder={translate(
                'mapView:viewRFIOverlayContent.discussion'
              )}
              hasNewActivity={rfi?.forwardactivity.size > 0}
            />
          </Tabs>
        </div>
      )}
      {renderContent()}
    </OverlayStdContainer>
  );
};

export default ViewRFIOverlayContent;
